// src/projection/colors.js
// ADR-038: cluster color system — golden-angle HSL with split-sibling
//          saturation variation for daughter clusters.
//
// This module is pure color math. No Three.js, no React, no Supabase.
// Consumers: `scene-adapter.js` populates a `colorByGroupId` Map from
// `applyData()`; Block 3 (ADR-035 neurons) reads that Map to color
// each cluster's neurons.
//
// Output convention: all HSL components normalized to [0, 1]. This
// matches `THREE.Color.setHSL(h, s, l)` directly. The ADR spec gives
// hue in degrees (0-360) for human readability; this file converts
// to [0, 1] internally so downstream Three.js code doesn't need to.
//
// Per S27 Q2 (2026-04-24): colors are computed at render time, not
// persisted. No DB migration. Input is the current `groups` array
// (already ordered by `created_at` ASC by the polling query); output
// is a Map keyed by `group_id`.
//
// History note: ADR-046 (2026-05-05) briefly replaced golden-angle HSL
// with the Tol Bright qualitative palette for colorblind safety, then
// was reverted 2026-05-06 per Julie aesthetic feedback (the brighter
// golden-angle colors read better on screen). ADR-046 §5 (the
// pickTextColor WCAG correction) is independent and remains active —
// see the comment block above pickTextColor below.

// Golden angle in degrees. Mathematically optimal for maximizing
// perceptual hue separation across an arbitrary cluster count.
const GOLDEN_ANGLE_DEG = 137.5

// ADR-038 §Decision 1: root-cluster saturation + lightness defaults.
// S30 Block 6 part 1 v3 (2026-04-25): bumped saturation 0.75 → 1.0 per
// Julie aesthetic feedback ("still pastel" after MeshStandardMaterial swap).
// S30 Block 6 part 2 v4 (2026-04-25): bumped lightness 0.55 → 0.65 per
// Julie aesthetic feedback ("brighter and more color"). Brighter colors
// directly via HSL — better than bumping emissive (which adds white
// wash). Cluster-flash peaks at 0.95 so the dynamic range compresses
// from 0.40 → 0.30 — the flash still punches because of the scale-pop
// AND the brighter baseline already reads more present.
const DEFAULT_SATURATION = 1.0
const DEFAULT_LIGHTNESS = 0.65

// ADR-038 §Decision 2: split-sibling saturation table.
// Index is depth of split tree (1 = direct daughters of root, 2 = nested
// daughters). Within each tier, daughters are ordered by created_at ASC:
// first daughter = sibling A, second = B, third (if any) = C.
// Tier 1: 0.75 / 0.85 (first = less-saturated, second = more-saturated).
// Tier 2: 0.65 / 0.95 (wider spread as tree deepens).
// ADR-040 caps split-tree depth at 3 leaves per topic; tier 3+ is
// disallowed upstream. This table returns undefined beyond tier 2 so a
// caller can catch the invariant-violation.
const SPLIT_SATURATIONS_BY_TIER = {
  1: [0.75, 0.85],
  2: [0.65, 0.95],
}

/**
 * Compute the golden-angle hue for a root cluster at zero-indexed
 * position `n`. Returned in [0, 1] (normalized from degrees).
 *
 * Example: rootHue(0) = 0.0 (red); rootHue(1) ≈ 0.381 (cyan-green);
 * rootHue(2) ≈ 0.764 (purple-blue).
 *
 * @param {number} n - zero-indexed creation order among root clusters.
 * @returns {number} hue in [0, 1]
 */
export function rootHue(n) {
  const deg = (n * GOLDEN_ANGLE_DEG) % 360
  return deg / 360
}

/**
 * Compute the HSL color for a root cluster at zero-indexed position `n`.
 * All components in [0, 1].
 *
 * @param {number} n
 * @returns {{h: number, s: number, l: number}}
 */
export function rootClusterColor(n) {
  return {
    h: rootHue(n),
    s: DEFAULT_SATURATION,
    l: DEFAULT_LIGHTNESS,
  }
}

/**
 * Compute the HSL color for a daughter cluster given its parent's color
 * and its position within the split tree.
 *
 * @param {{h: number, s: number, l: number}} parentColor - parent's HSL
 * @param {number} tier - depth of this daughter in the split tree (1 or 2 per ADR-040 cap)
 * @param {number} siblingIndex - zero-indexed position among siblings at this tier (0 = A, 1 = B)
 * @returns {{h: number, s: number, l: number}}
 */
export function daughterClusterColor(parentColor, tier, siblingIndex) {
  const table = SPLIT_SATURATIONS_BY_TIER[tier]
  if (!table) {
    // ADR-040 caps at 3 leaves, so tier 3+ is an invariant violation.
    // Fall back to parent saturation rather than crash — the visual
    // reads a little off but the projection doesn't die.
    // eslint-disable-next-line no-console
    console.warn(`[colors] daughterClusterColor: tier=${tier} exceeds ADR-040 split cap; using parent saturation.`)
    return { h: parentColor.h, s: parentColor.s, l: parentColor.l }
  }
  const s = table[Math.min(siblingIndex, table.length - 1)]
  return {
    h: parentColor.h, // daughters inherit parent hue (ADR-038 §Decision 2)
    s,
    l: parentColor.l,
  }
}

/**
 * Given the current `groups` array (from the projection polling query),
 * compute the color map keyed by group_id.
 *
 * Handles both root clusters and (once ADR-040 split logic ships)
 * daughter clusters via `parent_id`. For BSW all clusters are expected
 * to be roots — daughter handling is forward-compatible scaffolding,
 * not load-bearing at event time.
 *
 * Input groups should include at minimum: `id`, `created_at`, and
 * optionally `parent_id` (from ADR-040 migration `_016`; currently
 * pending — rows without the column get `undefined` and are treated
 * as roots).
 *
 * @param {Array<{id: string, created_at: string|number, parent_id?: string|null}>} groups
 * @returns {Map<string, {h: number, s: number, l: number}>}
 */
export function computeClusterColors(groups) {
  const byId = new Map()
  if (!groups || groups.length === 0) return byId

  // Normalize sort. The polling query already orders by created_at ASC,
  // but be defensive — a caller might pass a pre-filtered or differently-
  // ordered subset.
  const sorted = [...groups].sort((a, b) => {
    const ta = new Date(a.created_at).getTime()
    const tb = new Date(b.created_at).getTime()
    return ta - tb
  })

  // Pass 1: assign root hues in creation order, counting only roots.
  let rootIndex = 0
  for (const g of sorted) {
    if (!g.parent_id) {
      byId.set(g.id, rootClusterColor(rootIndex))
      rootIndex += 1
    }
  }

  // Pass 2: walk daughters, resolving parent hue and tier. Because
  // `sorted` is ASC by created_at and a child is always created after
  // its parent, any daughter's parent is already resolved by the time
  // we hit the daughter.
  //
  // Tier = depth from root (1 for direct daughter of a root, 2 for
  // nested daughter, etc.). We compute it by walking up parent chain.
  // Sibling index among same-parent same-tier: ordered by created_at
  // ASC within the parent's daughter set.
  const siblingCountByParent = new Map() // parent_id -> how many daughters assigned so far
  for (const g of sorted) {
    if (!g.parent_id) continue
    const parentColor = byId.get(g.parent_id)
    if (!parentColor) {
      // Orphan daughter — parent not in the input set. Treat as root.
      byId.set(g.id, rootClusterColor(rootIndex))
      rootIndex += 1
      continue
    }

    const tier = computeSplitTier(g, sorted)
    const siblingIndex = siblingCountByParent.get(g.parent_id) || 0
    siblingCountByParent.set(g.parent_id, siblingIndex + 1)

    byId.set(g.id, daughterClusterColor(parentColor, tier, siblingIndex))
  }

  return byId
}

/**
 * Depth of `group` in the split tree (1 = direct daughter of a root,
 * 2 = grand-daughter). Walks `parent_id` chain.
 *
 * @param {{id: string, parent_id?: string|null}} group
 * @param {Array<{id: string, parent_id?: string|null}>} allGroups
 * @returns {number}
 */
function computeSplitTier(group, allGroups) {
  const byIdLookup = new Map(allGroups.map((g) => [g.id, g]))
  let tier = 0
  let cur = group
  while (cur && cur.parent_id) {
    tier += 1
    cur = byIdLookup.get(cur.parent_id)
    if (tier > 10) break // runaway protection; ADR-040 caps real tier at 2
  }
  return tier
}

/**
 * Convenience: format a color object as a CSS `hsl(...)` string, for
 * debugging, logging, and any DOM surface that wants to reuse the
 * cluster palette.
 *
 * @param {{h: number, s: number, l: number}} color
 * @returns {string} e.g. "hsl(137.5, 75%, 55%)"
 */
export function hslString({ h, s, l }) {
  const deg = (h * 360).toFixed(1)
  const sp = (s * 100).toFixed(0)
  const lp = (l * 100).toFixed(0)
  return `hsl(${deg}, ${sp}%, ${lp}%)`
}

// ---- Foreground text color for cluster-color backgrounds -------------
// ADR-036 §S34 amendment shipped per-group cards with cluster-color
// header bars. Reversed-out white text fails on light cluster colors
// (high-saturation yellow / green / cyan at the bright palette have
// high WCAG luminance, which drops white text to insufficient
// contrast). This picks a foreground color per cluster: white for
// dark backgrounds, near-black for light.
//
// Same helper is reused by App.jsx GroupSkeleton in Block 5 (attendee
// surface cluster-color reveal — same WCAG constraint) and by
// PresentProjection.jsx right-rail group cards.
//
// Implementation: HSL → RGB → sRGB linear → Rec.709 relative luminance,
// then WCAG contrast ratio (L_lighter + 0.05) / (L_darker + 0.05).
// We compute the ratio for both white and near-black against the
// background and return whichever has the higher ratio. This is more
// correct than a fixed-luminance threshold; the mathematical crossover
// where white-on-bg and near-black-on-bg give equal WCAG contrast sits
// at background Y ≈ 0.179, not 0.5. The 0.5 heuristic was a common
// but incorrect mid-luminance assumption — backgrounds in the 0.18 to
// 0.50 range get black text under correct math, white under the old
// threshold. (Per ADR-046 §5, retained after 2026-05-06 palette revert
// because the math fix is palette-agnostic.)

function hslToRgb(h, s, l) {
  // h, s, l all in [0, 1]; returns [r, g, b] in [0, 1].
  if (s === 0) return [l, l, l] // achromatic
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1
    if (t > 1) t -= 1
    if (t < 1 / 6) return p + (q - p) * 6 * t
    if (t < 1 / 2) return q
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
    return p
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  return [hue2rgb(p, q, h + 1 / 3), hue2rgb(p, q, h), hue2rgb(p, q, h - 1 / 3)]
}

function relativeLuminance(r, g, b) {
  // sRGB → linear → Y per Rec.709 (WCAG 2.x relative luminance).
  const lin = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4))
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b)
}

// Near-black foreground color used by `pickTextColor`. Matched to the
// `#0a0a0a` value that callers paste into CSS so the contrast math
// uses the same color we actually render.
const NEAR_BLACK_HEX_CHANNEL = 10 / 255
const NEAR_BLACK_LUMINANCE = relativeLuminance(
  NEAR_BLACK_HEX_CHANNEL,
  NEAR_BLACK_HEX_CHANNEL,
  NEAR_BLACK_HEX_CHANNEL,
)
const WHITE_LUMINANCE = 1.0

/**
 * Pick a foreground text color (white or near-black) that has the
 * higher WCAG contrast against a given HSL background color.
 *
 * Per WCAG 2.x: contrast ratio = (L_lighter + 0.05) / (L_darker + 0.05).
 * We compute that ratio for both white-on-bg and near-black-on-bg, and
 * return whichever wins. The mathematical crossover where both are equal
 * is at background relative luminance ≈ 0.179, NOT 0.5 (the previous
 * heuristic). Backgrounds in the 0.18 to 0.50 range get black text;
 * backgrounds darker than ~0.18 get white text.
 *
 * @param {{h: number, s: number, l: number}} color - cluster HSL in [0,1]
 * @returns {string} CSS hex — '#ffffff' or '#0a0a0a'
 */
export function pickTextColor({ h, s, l }) {
  const [r, g, b] = hslToRgb(h, s, l)
  const Y_bg = relativeLuminance(r, g, b)

  const contrastWhite = (WHITE_LUMINANCE + 0.05) / (Y_bg + 0.05)
  const contrastBlack = (Y_bg + 0.05) / (NEAR_BLACK_LUMINANCE + 0.05)

  return contrastBlack >= contrastWhite ? '#0a0a0a' : '#ffffff'
}
